import React, { useState, useEffect } from 'react';
import { Link, useParams, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import { Menu, X, ChevronDown, Globe } from 'lucide-react';
import { Button } from '../ui/button';
import { cn, getLocalizedPath, SUPPORTED_LOCALES } from '../../lib/utils';

/**
 * Navbar sticky premium avec menu mobile, sélecteur de langue et animations
 */

/* Éléments de navigation */
const navItems = [
    { key: 'home', path: '' },
    { key: 'ecosystem', path: '/ecosysteme' },
    { key: 'services', path: '/services' },
    {
        key: 'more',
        children: [
            { key: 'kolaboEdu', path: '/kolabo-edu' },
            { key: 'kolaboHub', path: '/kolabo-hub' },
            { key: 'kolaboLab', path: '/kolabo-lab' },
        ],
    },
    { key: 'team', path: '/equipe' },
    { key: 'blog', path: '/blog' },
    { key: 'contact', path: '/contact' },
];

/* Drapeaux pour le sélecteur de langue */
const localeFlags: Record<string, { flag: string; label: string }> = {
    fr: { flag: '🇫🇷', label: 'Français' },
    en: { flag: '🇺🇸', label: 'English' },
    ht: { flag: '🇭🇹', label: 'Kreyòl' },
};

export default function Navbar() {
    const { t } = useTranslation();
    const { locale } = useParams<{ locale: string }>();
    const location = useLocation();
    const currentLocale = locale || 'fr';

    const [isScrolled, setIsScrolled] = useState(false);
    const [isMobileOpen, setIsMobileOpen] = useState(false);
    const [isLangOpen, setIsLangOpen] = useState(false);
    const [isMoreOpen, setIsMoreOpen] = useState(false);

    /* Détection du scroll pour le style de la navbar */
    useEffect(() => {
        const handleScroll = () => setIsScrolled(window.scrollY > 20);
        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    /* Fermer le menu mobile lors du changement de route */
    useEffect(() => {
        setIsMobileOpen(false);
        setIsMoreOpen(false);
    }, [location.pathname]);

    /* Vérifier si un lien est actif */
    const isActive = (path: string) => {
        const fullPath = getLocalizedPath(currentLocale, path);
        if (path === '') {
            return location.pathname === `/${currentLocale}` || location.pathname === `/${currentLocale}/`;
        }
        return location.pathname.startsWith(fullPath);
    };

    return (
        <motion.nav
            initial={{ y: -100 }}
            animate={{ y: 0 }}
            transition={{ duration: 0.5, ease: 'easeOut' }}
            className={cn(
                'fixed top-0 left-0 right-0 z-50 transition-all duration-300',
                isScrolled
                    ? 'bg-white/95 backdrop-blur-lg shadow-soft border-b border-navy-100'
                    : 'bg-transparent'
            )}
        >
            <div className="container-zig">
                <div className="flex items-center justify-between h-16 lg:h-20">
                    {/* Logo */}
                    <Link
                        to={getLocalizedPath(currentLocale, '')}
                        className="flex items-center gap-2 group"
                    >
                        <div className="w-10 h-10 rounded-xl bg-accent-gradient flex items-center justify-center shadow-glow group-hover:shadow-glow-lg transition-shadow duration-300">
                            <span className="text-white font-display font-bold text-lg"><img src="/zig.jpeg" alt="Zig Investment" className='w-10 h-10 rounded-xl' /></span>
                        </div>
                        <span className={cn(
                            'font-display font-bold text-xl transition-colors duration-300',
                            isScrolled ? 'text-navy-900' : 'text-white'
                        )}>
                            ZIG
                        </span>
                    </Link>

                    {/* Navigation desktop */}
                    <div className="hidden lg:flex items-center gap-1">
                        {navItems.map((item) =>
                            item.children ? (
                                <div key={item.key} className="relative">
                                    <button
                                        onClick={() => setIsMoreOpen(!isMoreOpen)}
                                        onBlur={() => setTimeout(() => setIsMoreOpen(false), 200)}
                                        className={cn(
                                            'flex items-center gap-1 px-4 py-2 rounded-xl text-sm font-medium transition-all duration-200',
                                            isScrolled
                                                ? 'text-navy-600 hover:text-accent-600 hover:bg-navy-50'
                                                : 'text-white/80 hover:text-white hover:bg-white/10'
                                        )}
                                    >
                                        Plus
                                        <ChevronDown className={cn('w-4 h-4 transition-transform duration-200', isMoreOpen && 'rotate-180')} />
                                    </button>
                                    <AnimatePresence>
                                        {isMoreOpen && (
                                            <motion.div
                                                initial={{ opacity: 0, y: 10, scale: 0.95 }}
                                                animate={{ opacity: 1, y: 0, scale: 1 }}
                                                exit={{ opacity: 0, y: 10, scale: 0.95 }}
                                                transition={{ duration: 0.2 }}
                                                className="absolute top-full left-0 mt-2 w-52 bg-white rounded-2xl shadow-soft-lg border border-navy-100 overflow-hidden"
                                            >
                                                {item.children.map((child) => (
                                                    <Link
                                                        key={child.key}
                                                        to={getLocalizedPath(currentLocale, child.path!)}
                                                        className={cn(
                                                            'block px-4 py-3 text-sm font-medium transition-colors duration-200',
                                                            isActive(child.path!)
                                                                ? 'text-accent-600 bg-accent-50'
                                                                : 'text-navy-600 hover:text-accent-600 hover:bg-navy-50'
                                                        )}
                                                    >
                                                        {t(`nav.${child.key}`)}
                                                    </Link>
                                                ))}
                                            </motion.div>
                                        )}
                                    </AnimatePresence>
                                </div>
                            ) : (
                                <Link
                                    key={item.key}
                                    to={getLocalizedPath(currentLocale, item.path!)}
                                    className={cn(
                                        'px-4 py-2 rounded-xl text-sm font-medium transition-all duration-200',
                                        isActive(item.path!)
                                            ? isScrolled
                                                ? 'text-accent-600 bg-accent-50'
                                                : 'text-white bg-white/20'
                                            : isScrolled
                                                ? 'text-navy-600 hover:text-accent-600 hover:bg-navy-50'
                                                : 'text-white/80 hover:text-white hover:bg-white/10'
                                    )}
                                >
                                    {t(`nav.${item.key}`)}
                                </Link>
                            )
                        )}
                    </div>

                    {/* Actions desktop */}
                    <div className="hidden lg:flex items-center gap-3">
                        {/* Sélecteur de langue */}
                        <div className="relative">
                            <button
                                onClick={() => setIsLangOpen(!isLangOpen)}
                                onBlur={() => setTimeout(() => setIsLangOpen(false), 200)}
                                className={cn(
                                    'flex items-center gap-2 px-3 py-2 rounded-xl text-sm font-medium transition-all duration-200',
                                    isScrolled
                                        ? 'text-navy-600 hover:bg-navy-50'
                                        : 'text-white/80 hover:bg-white/10'
                                )}
                                aria-label={t('nav.language')}
                            >
                                <Globe className="w-4 h-4" />
                                <span>{localeFlags[currentLocale]?.flag}</span>
                                <ChevronDown className={cn('w-3 h-3 transition-transform duration-200', isLangOpen && 'rotate-180')} />
                            </button>
                            <AnimatePresence>
                                {isLangOpen && (
                                    <motion.div
                                        initial={{ opacity: 0, y: 10, scale: 0.95 }}
                                        animate={{ opacity: 1, y: 0, scale: 1 }}
                                        exit={{ opacity: 0, y: 10, scale: 0.95 }}
                                        transition={{ duration: 0.2 }}
                                        className="absolute top-full right-0 mt-2 w-44 bg-white rounded-2xl shadow-soft-lg border border-navy-100 overflow-hidden"
                                    >
                                        {SUPPORTED_LOCALES.map((loc) => {
                                            // Reconstruire le chemin avec la nouvelle locale
                                            const restPath = location.pathname.replace(`/${currentLocale}`, '') || '';
                                            return (
                                                <Link
                                                    key={loc}
                                                    to={`/${loc}${restPath}`}
                                                    className={cn(
                                                        'flex items-center gap-3 px-4 py-3 text-sm font-medium transition-colors duration-200',
                                                        loc === currentLocale
                                                            ? 'text-accent-600 bg-accent-50'
                                                            : 'text-navy-600 hover:text-accent-600 hover:bg-navy-50'
                                                    )}
                                                >
                                                    <span className="text-lg">{localeFlags[loc].flag}</span>
                                                    {localeFlags[loc].label}
                                                </Link>
                                            );
                                        })}
                                    </motion.div>
                                )}
                            </AnimatePresence>
                        </div>

                        <Button size="sm" asChild>
                            <Link to={getLocalizedPath(currentLocale, '/contact')}>
                                {t('nav.cta')}
                            </Link>
                        </Button>
                    </div>

                    {/* Bouton hamburger mobile */}
                    <button
                        onClick={() => setIsMobileOpen(!isMobileOpen)}
                        className={cn(
                            'lg:hidden p-2 rounded-xl transition-colors duration-200',
                            isScrolled ? 'text-navy-800' : 'text-white'
                        )}
                        aria-label="Menu"
                    >
                        {isMobileOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
                    </button>
                </div>

                {/* Menu mobile */}
                <AnimatePresence>
                    {isMobileOpen && (
                        <motion.div
                            initial={{ opacity: 0, height: 0 }}
                            animate={{ opacity: 1, height: 'auto' }}
                            exit={{ opacity: 0, height: 0 }}
                            transition={{ duration: 0.3 }}
                            className="lg:hidden overflow-hidden bg-white rounded-2xl shadow-soft-lg mb-4 border border-navy-100"
                        >
                            <div className="p-4 space-y-1">
                                {navItems.map((item) =>
                                    item.children ? (
                                        <div key={item.key}>
                                            {item.children.map((child) => (
                                                <Link
                                                    key={child.key}
                                                    to={getLocalizedPath(currentLocale, child.path!)}
                                                    className={cn(
                                                        'block px-4 py-3 rounded-xl text-sm font-medium transition-colors duration-200',
                                                        isActive(child.path!)
                                                            ? 'text-accent-600 bg-accent-50'
                                                            : 'text-navy-600 hover:text-accent-600 hover:bg-navy-50'
                                                    )}
                                                >
                                                    {t(`nav.${child.key}`)}
                                                </Link>
                                            ))}
                                        </div>
                                    ) : (
                                        <Link
                                            key={item.key}
                                            to={getLocalizedPath(currentLocale, item.path!)}
                                            className={cn(
                                                'block px-4 py-3 rounded-xl text-sm font-medium transition-colors duration-200',
                                                isActive(item.path!)
                                                    ? 'text-accent-600 bg-accent-50'
                                                    : 'text-navy-600 hover:text-accent-600 hover:bg-navy-50'
                                            )}
                                        >
                                            {t(`nav.${item.key}`)}
                                        </Link>
                                    )
                                )}

                                <div className="border-t border-navy-100 pt-3 mt-3">
                                    {/* Langues mobile */}
                                    <div className="flex gap-2 mb-3 px-4">
                                        {SUPPORTED_LOCALES.map((loc) => {
                                            const restPath = location.pathname.replace(`/${currentLocale}`, '') || '';
                                            return (
                                                <Link
                                                    key={loc}
                                                    to={`/${loc}${restPath}`}
                                                    className={cn(
                                                        'flex items-center gap-1 px-3 py-2 rounded-xl text-sm font-medium transition-colors duration-200',
                                                        loc === currentLocale
                                                            ? 'bg-accent-500 text-white'
                                                            : 'bg-navy-50 text-navy-600'
                                                    )}
                                                >
                                                    <span>{localeFlags[loc].flag}</span>
                                                    <span className="text-xs">{localeFlags[loc].label}</span>
                                                </Link>
                                            );
                                        })}
                                    </div>
                                    <div className="px-4">
                                        <Button className="w-full" asChild>
                                            <Link to={getLocalizedPath(currentLocale, '/contact')}>
                                                {t('nav.cta')}
                                            </Link>
                                        </Button>
                                    </div>
                                </div>
                            </div>
                        </motion.div>
                    )}
                </AnimatePresence>
            </div>
        </motion.nav>
    );
}
